import React, { useCallback, useMemo, useState } from "react";
import { Activity, Brain, ChevronDown, ChevronUp, Download, FileText, History, RefreshCw, ShieldAlert } from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "../ui/tooltip";
import { formatDateDisplay, formatTimeDisplay, getBrowserTimezone, resolveDisplayTimezone } from "../../lib/datetime";
import { getTradeAudit } from "../../services/api.trading";
import { buildApiUrl } from "../../services/apiBase";
import { useAuth } from "../../context/AuthContext";

const DASHBOARD_TYPOGRAPHY = {
  sectionTitle: "text-xs font-semibold uppercase tracking-[0.18em] text-white/65",
  body: "text-sm font-medium text-white",
  fine: "text-xs text-white/60",
};

const BROWSER_TIMEZONE = getBrowserTimezone();

const formatPriceValue = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return "—";
  return numeric.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatCurrencyValue = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return "—";
  return numeric.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatSignedCurrencyValue = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "—";
  return `${numeric >= 0 ? "+" : "-"}${Math.abs(numeric).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const toFiniteNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const firstFiniteNumber = (...values) => {
  for (const value of values) {
    const numeric = toFiniteNumber(value);
    if (numeric !== null) return numeric;
  }
  return null;
};

const formatEntryTimeValue = (value, timeZone) => {
  if (!value) return "—";
  return formatTimeDisplay(value, timeZone || BROWSER_TIMEZONE, { hour12: false, timeZoneName: "short" }) || "—";
};

const formatTradeCardTimeValue = (value, timeZone) => {
  if (!value) return "—";
  const resolvedTimeZone = timeZone || BROWSER_TIMEZONE;
  const dateLabel = formatDateDisplay(value, resolvedTimeZone, {
    year: null,
    month: "short",
    day: "numeric",
  });
  const timeLabel = formatTimeDisplay(value, resolvedTimeZone, {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZoneName: "short",
  });
  if (!dateLabel && !timeLabel) return "—";
  if (!dateLabel) return timeLabel || "—";
  if (!timeLabel) return dateLabel || "—";
  return `${dateLabel} - ${timeLabel}`;
};

const formatAuditTimestampValue = (value, timeZone) => {
  if (!value) return "—";
  return formatTimeDisplay(value, timeZone || BROWSER_TIMEZONE, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZoneName: "shortGeneric",
  }) || "—";
};

const formatTimelineTypeLabel = (value) => {
  if (!value) return "Unknown event";
  return String(value)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
};

const safeParseObject = (value) => {
  if (!value) return {};
  if (typeof value === "object") return value;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (_error) {
    return {};
  }
};

const formatContractsLabel = (quantity) => {
  const safeQuantity = Math.max(1, Number(quantity) || 1);
  return `${safeQuantity} Contract${safeQuantity === 1 ? "" : "s"}`;
};

const sanitizeTradeReasonPart = (value) => {
  if (value == null) return "";
  return String(value).replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
};

const buildTradeReasonDisplay = (trade, strategyName) => {
  const explicitReason = sanitizeTradeReasonPart(trade?.entry_reason);
  if (explicitReason) return explicitReason;

  const parts = [
    sanitizeTradeReasonPart(trade?.entry_source_label),
    sanitizeTradeReasonPart(strategyName),
    trade?.entry_interval_minutes ? `${trade.entry_interval_minutes}m` : "",
    sanitizeTradeReasonPart(trade?.entry_session),
  ].filter(Boolean);

  const uniqueParts = parts.filter((part, index) => parts.indexOf(part) === index);
  if (uniqueParts.length) return uniqueParts.join(" • ");

  return sanitizeTradeReasonPart(trade?.execution_source) || "";
};

const humanizeReasonCode = (value) => {
  const cleaned = sanitizeTradeReasonPart(value);
  if (!cleaned) return "";
  return cleaned.toLowerCase().replace(/\b\w/g, (character) => character.toUpperCase());
};

const getTimelineEvents = (payload) => (
  Array.isArray(payload?.timeline) ? payload.timeline : []
);

const getAuditScalar = (value) => {
  if (["string", "number"].includes(typeof value)) return sanitizeTradeReasonPart(value);
  if (!value || typeof value !== "object") return "";
  return sanitizeTradeReasonPart(value.label || value.name || value.session || value.value);
};

export const groupTimelineEvents = (events = []) => {
  const grouped = [];
  events.forEach((event) => {
    const details = safeParseObject(event?.details);
    const signature = JSON.stringify({
      type: event?.type || "",
      status: event?.status || "",
      broker_status: event?.broker_status || "",
      mechanism_key: details?.mechanism_key || "",
      action: details?.action || "",
      reason: details?.reason || details?.reason_code || "",
    });
    const previous = grouped[grouped.length - 1];
    if (previous?._groupSignature === signature) {
      previous._repeatCount += 1;
      previous._lastAt = event?.at || previous._lastAt;
      return;
    }
    grouped.push({
      ...event,
      _groupSignature: signature,
      _repeatCount: 1,
      _lastAt: event?.at,
    });
  });
  return grouped;
};

const findLatestTimelineEvent = (payload, types) => {
  const wanted = new Set(types);
  const events = getTimelineEvents(payload);
  for (let index = events.length - 1; index >= 0; index -= 1) {
    if (wanted.has(events[index]?.type)) return events[index];
  }
  return null;
};

const buildTradeExitInfo = (tradeOrAuditPayload) => {
  const brokerPayload = safeParseObject(
    tradeOrAuditPayload?.broker_payload || tradeOrAuditPayload?.brokerPayload,
  );
  const auditSummary = tradeOrAuditPayload?.audit_summary || {};
  const strategyExitEvent = findLatestTimelineEvent(brokerPayload, [
    "strategy_exit_queued",
    "strategy_exit_completed",
    "strategy_exit_blocked",
  ]);
  const stopSelectedEvent = findLatestTimelineEvent(brokerPayload, ["trade_management_stop_selected"]);
  const eventExitDetails = safeParseObject(strategyExitEvent?.details?.exit_details);
  const savedExitDetails = safeParseObject(brokerPayload?.strategy_exit_details);
  const exitDetails = Object.keys(savedExitDetails).length ? savedExitDetails : eventExitDetails;
  const exitReason =
    exitDetails?.exit_reason ||
    brokerPayload?.strategy_exit_reason ||
    strategyExitEvent?.details?.exit_reason ||
    brokerPayload?.last_exit_reason ||
    brokerPayload?.close_reason ||
    auditSummary?.close_reason ||
    tradeOrAuditPayload?.close_reason;
  const mechanism =
    exitDetails?.stop_mechanism ||
    exitDetails?.last_confirmed_stop_reason ||
    exitDetails?.pending_stop_reason ||
    exitDetails?.stop_source ||
    stopSelectedEvent?.details?.mechanism_key ||
    stopSelectedEvent?.details?.mechanism_name;
  const lines = [];
  if (mechanism) lines.push(`Mechanism: ${humanizeReasonCode(mechanism)}`);
  if (exitDetails?.trigger_check) {
    const current = formatPriceValue(exitDetails.current_price);
    const level = formatPriceValue(exitDetails.trigger_level);
    lines.push(`Trigger: ${exitDetails.trigger_check}${current !== "—" ? ` at ${current}` : ""}${level !== "—" ? ` vs ${level}` : ""}`);
  }
  if (exitDetails?.broker_stop_price || exitDetails?.protective_stop_price) {
    lines.push(`Broker stop: ${formatPriceValue(exitDetails.broker_stop_price || exitDetails.protective_stop_price)}`);
  } else if (stopSelectedEvent?.details?.candidate_stop) {
    lines.push(`Selected stop: ${formatPriceValue(stopSelectedEvent.details.candidate_stop)}`);
  }
  if (brokerPayload?.close_source) lines.push(`Close source: ${humanizeReasonCode(brokerPayload.close_source)}`);
  return {
    label: humanizeReasonCode(exitReason) || "—",
    lines: lines.filter(Boolean),
  };
};

const buildAuditEntryInfo = (auditPayload) => {
  const strategySnapshot = auditPayload?.strategy_snapshot || {};
  const trade = auditPayload?.trade || {};
  const brokerPayload = safeParseObject(auditPayload?.broker_payload);
  const context = strategySnapshot?.context || {};
  const source = strategySnapshot?.source || trade?.execution_source || brokerPayload?.entry_source;
  const parts = [
    sanitizeTradeReasonPart(source),
    sanitizeTradeReasonPart(strategySnapshot?.strategy_name || brokerPayload?.linked_strategy_name),
    strategySnapshot?.interval_minutes ? `${strategySnapshot.interval_minutes}m` : "",
    getAuditScalar(context?.session),
  ].filter(Boolean);
  const uniqueParts = parts.filter((part, index) => parts.indexOf(part) === index);
  const lines = [];
  if (strategySnapshot?.signal_id) lines.push(`Signal: ${strategySnapshot.signal_id}`);
  if (context?.trigger_source) lines.push(`Trigger source: ${humanizeReasonCode(context.trigger_source)}`);
  if (context?.signal_state || context?.state) lines.push(`State: ${humanizeReasonCode(context.signal_state || context.state)}`);
  return {
    label: uniqueParts.length ? uniqueParts.join(" • ") : sanitizeTradeReasonPart(trade?.execution_source) || "—",
    lines,
  };
};

/**
 * PositionsPanel
 *
 * Props:
 *   positions               - array of open position objects
 *   closedPositions         - array of recently closed trade objects
 *   onClose                 - (positionId) => void — called when user clicks Close on a position
 *   getDisplayedPositionPnl - (position) => number — live P&L resolver passed from Dashboard
 */
const PositionsPanel = ({
  positions,
  closedPositions = [],
  realtimePnls = {},
  onClose,
  onFlatAll,
  onSyncStop,
  onReProtect,
  getDisplayedPositionPnl,
  timeZone,
}) => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const displayTimeZone = resolveDisplayTimezone(timeZone || BROWSER_TIMEZONE);
  const [auditTradeId, setAuditTradeId] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditPayload, setAuditPayload] = useState(null);
  const [showAllTrades, setShowAllTrades] = useState(false);
  const [expandedTradeIds, setExpandedTradeIds] = useState(() => new Set());
  const [expandedAuditEvents, setExpandedAuditEvents] = useState(() => new Set());
  const toggleExpandedTrade = useCallback((tradeId) => {
    setExpandedTradeIds((current) => {
      const next = new Set(current);
      if (next.has(tradeId)) next.delete(tradeId);
      else next.add(tradeId);
      return next;
    });
  }, []);
  const toggleExpandedAuditEvent = useCallback((eventKey) => {
    setExpandedAuditEvents((current) => {
      const next = new Set(current);
      if (next.has(eventKey)) next.delete(eventKey);
      else next.add(eventKey);
      return next;
    });
  }, []);
  const groupedPositions = useMemo(() => {
    const safePositions = Array.isArray(positions) ? positions : [];
    const groups = new Map();

    safePositions.forEach((position) => {
      const groupKey = [
        position?.symbol || "",
        String(position?.side || "").toUpperCase(),
        position?.execution_mode || "",
      ].join(":");

      if (!groups.has(groupKey)) {
        groups.set(groupKey, []);
      }
      groups.get(groupKey).push(position);
    });

    return Array.from(groups.values()).map((group) => {
      const sortedEntries = [...group].sort((left, right) => {
        const leftTime = new Date(left?.opened_at || 0).getTime();
        const rightTime = new Date(right?.opened_at || 0).getTime();
        return leftTime - rightTime;
      });

      const totalQuantity = group.reduce(
        (sum, position) => sum + (Number(position?.quantity) || 0),
        0,
      );
      const weightedEntryNumerator = group.reduce((sum, position) => {
        const quantity = Number(position?.quantity) || 0;
        const entryPrice = Number(position?.entry_price) || 0;
        return sum + (quantity * entryPrice);
      }, 0);
      const aggregateEntryPrice = totalQuantity > 0 ? weightedEntryNumerator / totalQuantity : 0;
      const livePnl = group.reduce(
        (sum, position) => {
          const streamingPnl = realtimePnls[position.id];
          const resolvedPnl = streamingPnl !== undefined ? streamingPnl : getDisplayedPositionPnl(position);
          return sum + (Number(resolvedPnl) || 0);
        },
        0,
      );
      const pointValue = firstFiniteNumber(...group.map((position) => position?.point_value));
      const notional = group.reduce((sum, position) => {
        const explicit = firstFiniteNumber(position?.position_notional, position?.position_value);
        if (explicit !== null) return sum + explicit;
        const quantity = Number(position?.quantity) || 0;
        const entryPrice = Number(position?.entry_price) || 0;
        const pointValueValue = firstFiniteNumber(position?.point_value, 1) ?? 1;
        return sum + (quantity * entryPrice * pointValueValue);
      }, 0);

      const stopValues = Array.from(
        new Set(
          group
            .map((position) => firstFiniteNumber(position?.stop_loss, position?.stop_loss_price))
            .filter((value) => value !== null)
            .map((value) => Number(value).toFixed(2)),
        ),
      );
      const targetValues = Array.from(
        new Set(
          group
            .map((position) => firstFiniteNumber(position?.take_profit, position?.take_profit_price))
            .filter((value) => value !== null)
            .map((value) => Number(value).toFixed(2)),
        ),
      );
      const strategyNames = Array.from(new Set(group.map((position) => position?.strategy_name).filter(Boolean)));
      const entryReasons = Array.from(
        new Set(
          group
            .map((position) => buildTradeReasonDisplay(position, position?.strategy_name))
            .filter(Boolean),
        ),
      );
      const brokerStatuses = Array.from(new Set(group.map((position) => position?.broker_status).filter(Boolean)));
      const protectionWarnings = Array.from(new Set(group.map((position) => position?.protection_warning).filter(Boolean)));
      const brokerSyncWarnings = Array.from(new Set(group.map((position) => position?.broker_sync_warning).filter(Boolean)));
      const stopProtectionStatuses = Array.from(new Set(group.map((position) => position?.protective_stop_status).filter(Boolean)));
      const targetProtectionStatuses = Array.from(new Set(group.map((position) => position?.take_profit_status).filter(Boolean)));
      const brokerPositionIds = Array.from(new Set(group.map((position) => position?.broker_position_id).filter(Boolean)));
      const entryOrderIds = Array.from(new Set(group.map((position) => position?.broker_entry_order_id || position?.broker_order_id).filter(Boolean)));
      const stopOrderIds = Array.from(new Set(group.map((position) => position?.broker_stop_order_id || position?.protective_stop_order_id).filter(Boolean)));
      const brokerStopPrices = Array.from(
        new Set(
          group
            .map((position) => firstFiniteNumber(position?.broker_confirmed_stop_price))
            .filter((value) => value !== null)
            .map((value) => Number(value).toFixed(2)),
        ),
      );
      const intendedStopPrices = Array.from(
        new Set(
          group
            .map((position) => firstFiniteNumber(position?.intended_stop_price))
            .filter((value) => value !== null)
            .map((value) => Number(value).toFixed(2)),
        ),
      );
      const allStopsBrokerSubmitted = group.every(
        (position) => firstFiniteNumber(position?.stop_loss, position?.stop_loss_price) === null || position?.stop_loss_broker_submitted,
      );
      const allTargetsBrokerSubmitted = group.every(
        (position) => firstFiniteNumber(position?.take_profit, position?.take_profit_price) === null || position?.take_profit_broker_submitted,
      );
      // Roll up the per-position protection_status enum into a single
      // worst-case status for the group: failure beats pending beats
      // unprotected beats confirmed beats paper.
      const protectionStatusList = group
        .map((position) => String(position?.protection_status || "").trim().toLowerCase())
        .filter(Boolean);
      const protectionStatusPriority = [
        "broker_stop_failed_internal_fallback",
        "unprotected",
        "broker_stop_pending",
        "broker_stop_confirmed",
        "paper",
      ];
      const groupProtectionStatus =
        protectionStatusPriority.find((status) => protectionStatusList.includes(status))
        || protectionStatusList[0]
        || null;
      const executionSources = Array.from(new Set(group.map((position) => position?.execution_source).filter(Boolean)));

      if (process.env.NODE_ENV === "development") {
        console.log(`[DEBUG] PositionsPanel Group ${group[0]?.symbol}:`, {
          executionMode: group[0]?.execution_mode,
          protectionStatus: groupProtectionStatus,
          isAdmin,
          accountName: group[0]?.account_name,
          accountId: group[0]?.trading_account_id,
          intendedStopPrice: intendedStopPrices.length === 1 ? Number(intendedStopPrices[0]) : null,
        });
      }

      return {
        key: `${group[0]?.symbol || "unknown"}:${group[0]?.side || "side"}`,
        positions: group,
        symbol: group[0]?.symbol || "—",
        side: String(group[0]?.side || "").toUpperCase(),
        quantity: totalQuantity,
        entryPrice: aggregateEntryPrice,
        livePnl,
        pointValue,
        notional,
        stopDisplay: stopValues.length === 1 ? formatPriceValue(stopValues[0]) : stopValues.length > 1 ? "Mixed" : "—",
        targetDisplay: targetValues.length === 1 ? formatPriceValue(targetValues[0]) : targetValues.length > 1 ? "Mixed" : "—",
        executionMode: group[0]?.execution_mode || "paper",
        status: group[0]?.status || "open",
        brokerStatuses,
        protectionWarnings,
        brokerSyncWarnings,
        stopProtectionStatuses,
        targetProtectionStatuses,
        brokerPositionIds,
        entryOrderIds,
        stopOrderIds,
        brokerStopDisplay: brokerStopPrices.length === 1 ? formatPriceValue(brokerStopPrices[0]) : brokerStopPrices.length > 1 ? "Mixed" : "—",
        brokerConfirmedStopPrice: brokerStopPrices.length === 1 ? Number(brokerStopPrices[0]) : null,
        intendedStopPrice: intendedStopPrices.length === 1 ? Number(intendedStopPrices[0]) : null,
        allStopsBrokerSubmitted,
        allTargetsBrokerSubmitted,
        protectionStatus: groupProtectionStatus,
        executionSources,
        strategyNames,
        entryReasons,
        entries: sortedEntries.map((position) => ({
          id: position.id,
          quantity: Number(position?.quantity) || 0,
          openedAtLabel: formatEntryTimeValue(position?.opened_at || position?.entry_time, displayTimeZone),
        })),
      };
    });
  }, [displayTimeZone, getDisplayedPositionPnl, positions]);

  // All status=closed trades sorted newest-first (unsliced — used for count + display)
  const allClosedSorted = useMemo(() => {
    return (Array.isArray(closedPositions) ? closedPositions : [])
      .filter((trade) => String(trade?.status || "").toLowerCase() === "closed")
      .sort((left, right) => {
        const leftTime = new Date(left?.exit_time || left?.closed_at || left?.created_at || 0).getTime();
        const rightTime = new Date(right?.exit_time || right?.closed_at || right?.created_at || 0).getTime();
        return rightTime - leftTime;
      });
  }, [closedPositions]);

  const recentClosedPositions = useMemo(() => {
    return (showAllTrades ? allClosedSorted : allClosedSorted.slice(0, 6))
      .map((trade) => {
        try {
          const brokerPayload = safeParseObject(trade?.broker_payload);
          const strategyName =
            trade?.strategy_name ||
            trade?.strategy_snapshot?.strategy_name ||
            trade?.audit_summary?.strategy_name ||
            brokerPayload?.linked_strategy_name ||
            "";

          return {
            brokerPayload,
            strategyName,
            entryReason: buildTradeReasonDisplay(trade, strategyName),
            id: trade?.id,
            auditTradeId: trade?.audit_trade_id || trade?.id,
            canAudit: !String(trade?.execution_source || "").toLowerCase().includes("broker_history"),
            symbol: trade?.symbol || "—",
            side: String(trade?.side || "").toUpperCase() || "—",
            quantity: Math.max(1, Number(trade?.quantity) || 1),
            brokerOrderId: trade?.broker_order_id || "",
            historyLegIndex: Number(trade?.history_entry_leg_index) || null,
            historyLegTotal: Number(trade?.history_entry_leg_total) || null,
            entryPrice: firstFiniteNumber(trade?.entry_price),
            exitPrice: firstFiniteNumber(trade?.exit_price),
            pnl: firstFiniteNumber(trade?.pnl),
            contractsLabel: formatContractsLabel(trade?.quantity),
            entryAtLabel: formatTradeCardTimeValue(
              trade?.entry_time || trade?.opened_at || trade?.created_at,
              displayTimeZone,
            ),
            closedAtLabel: formatTradeCardTimeValue(
              trade?.exit_time || trade?.closed_at || trade?.created_at,
              displayTimeZone,
            ),
            executionMode: trade?.execution_mode || "",
            executionSourceLabel: trade?.entry_source_label || "",
            pointValue: firstFiniteNumber(trade?.point_value),
            notional: firstFiniteNumber(trade?.position_notional, trade?.position_value),
            stopDisplay: formatPriceValue(firstFiniteNumber(trade?.stop_loss)),
            targetDisplay: formatPriceValue(firstFiniteNumber(trade?.take_profit)),
            closeReasonInfo: buildTradeExitInfo(trade),
          };
        } catch (_error) {
          return null;
        }
      })
      .filter(Boolean);
  }, [allClosedSorted, showAllTrades, displayTimeZone]);

  const handleOpenTradeAudit = useCallback(async (tradeId) => {
    if (!tradeId) return;
    setAuditTradeId(tradeId);
    setAuditLoading(true);
    try {
      const response = await getTradeAudit(tradeId);
      setAuditPayload(response?.data || null);
    } catch (_error) {
      setAuditPayload({
        error: "Trade audit is unavailable right now. Please try again.",
      });
    } finally {
      setAuditLoading(false);
    }
  }, []);

  const timelineEvents = useMemo(() => groupTimelineEvents(
    Array.isArray(auditPayload?.broker_payload?.timeline)
      ? auditPayload.broker_payload.timeline
      : [],
  ), [auditPayload]);
  const executionJobs = Array.isArray(auditPayload?.execution_jobs)
    ? auditPayload.execution_jobs
    : [];
  const auditEntryInfo = useMemo(() => (
    auditPayload ? buildAuditEntryInfo(auditPayload) : { label: "—", lines: [] }
  ), [auditPayload]);
  const auditExitInfo = useMemo(() => (
    auditPayload ? buildTradeExitInfo(auditPayload) : { label: "—", lines: [] }
  ), [auditPayload]);

  return (
    <Card className="bg-white/5 border-white/10">
      <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
        <CardTitle className={`${DASHBOARD_TYPOGRAPHY.sectionTitle} flex items-center gap-2`}>
          <Activity className="h-4 w-4" />
          Positions
        </CardTitle>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.open(buildApiUrl(`/api/reporting/trades/csv?token=${localStorage.getItem('token')}`), '_blank')}
            className="h-7 px-2 text-[10px] uppercase tracking-wider font-bold border-white/10 hover:bg-white/5"
          >
            <Download className="w-3 h-3 mr-1" />
            CSV
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.open(buildApiUrl(`/api/reporting/trades/pdf?token=${localStorage.getItem('token')}`), '_blank')}
            className="h-7 px-2 text-[10px] uppercase tracking-wider font-bold border-white/10 hover:bg-white/5"
          >
            <FileText className="w-3 h-3 mr-1" />
            PDF
          </Button>
          {groupedPositions.length > 0 && (
            <Button
              variant="destructive"
              size="sm"
              onClick={onFlatAll}
              className="h-7 px-2 text-[10px] uppercase tracking-wider font-bold"
            >
              Flat All
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-4">
        <div className="space-y-4">
          <div>
            {groupedPositions.length === 0 ? (
              <div className="text-center py-4 text-white/70 text-sm">
                No open positions
              </div>
            ) : (
              <div className="space-y-2">
                {groupedPositions.map((group) => {
                  const protectionLabelMap = {
                    broker_stop_confirmed: { label: "Broker Stop Confirmed", className: "border-emerald-500/30 bg-emerald-500/15 text-emerald-200" },
                    broker_stop_pending: { label: "Broker Stop Pending", className: "border-yellow-400/30 bg-yellow-400/15 text-yellow-200" },
                    broker_stop_failed_internal_fallback: { label: "Broker Stop Failed - Internal Fallback", className: "border-red-500/40 bg-red-500/15 text-red-200" },
                    unprotected: { label: "Unprotected - Action Required", className: "border-red-500/40 bg-red-500/20 text-red-100" },
                    paper: { label: "Paper", className: "border-white/15 bg-white/10 text-white/75" },
                  };
                  const protectionView = group.protectionStatus ? protectionLabelMap[group.protectionStatus] : null;
                  return (
                    <div key={group.key} className="rounded bg-white/5 p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className={DASHBOARD_TYPOGRAPHY.body}>{group.symbol}</div>
                          <div className={DASHBOARD_TYPOGRAPHY.fine}>{group.side} {group.quantity}</div>
                          {group.strategyNames.length ? (
                            <div className="mt-1 text-[11px] text-blue-300">{group.strategyNames.join(" • ")}</div>
                          ) : null}
                          {group.entryReasons.length ? (
                            <div className="mt-1 text-[11px] text-white/70">
                              <span className="mr-1 uppercase tracking-[0.14em] text-white/55">Entry reason</span>
                              <span>{group.entryReasons.join(" / ")}</span>
                            </div>
                          ) : null}
                          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                            {protectionView ? (
                              <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-wider ${protectionView.className}`}>
                                {protectionView.label}
                              </span>
                            ) : null}
                            {group.executionSources.length ? (
                              <span className="inline-flex items-center rounded border border-white/15 bg-black/30 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-white/65">
                                {group.executionSources.join(" / ")}
                              </span>
                            ) : null}
                          </div>
                        </div>
                        <Button
                          onClick={async () => {
                            for (const position of group.positions) {
                              await onClose(position.id);
                            }
                          }}
                          size="sm"
                          variant="outline"
                          className="bg-red-500/10 border-red-500/20 text-red-500 hover:bg-red-500/20"
                        >
                          Close
                        </Button>
                      </div>
                      <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2">
                        <div>
                          <div className="text-[10px] uppercase tracking-[0.14em] text-white/65">Entry</div>
                          <div className="text-sm text-white">{formatPriceValue(group.entryPrice)}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-[0.14em] text-white/65">P&amp;L</div>
                          <div className={`text-sm ${group.livePnl >= 0 ? "text-green-400" : "text-red-400"}`}>
                            {formatSignedCurrencyValue(group.livePnl)}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-[0.14em] text-white/65">Point Value</div>
                          <div className="text-sm text-white">
                            {group.pointValue !== null ? `${formatCurrencyValue(group.pointValue)} / pt` : "—"}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-[0.14em] text-white/65">Notional</div>
                          <div className="text-sm text-white">{formatCurrencyValue(group.notional)}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-[0.14em] text-white/65">Stop</div>
                          <div className="text-sm text-white">{group.stopDisplay}</div>
                          {group.stopDisplay !== "—" && !group.allStopsBrokerSubmitted ? (
                            <div className="mt-0.5 text-[10px] uppercase tracking-[0.1em] text-yellow-300">
                              Intended, not broker-protected
                            </div>
                          ) : null}
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-[0.14em] text-white/65">Target</div>
                          <div className="text-sm text-white">{group.targetDisplay}</div>
                          {group.targetDisplay !== "—" && !group.allTargetsBrokerSubmitted ? (
                            <div className="mt-0.5 text-[10px] uppercase tracking-[0.1em] text-yellow-300">
                              Intended, not broker-protected
                            </div>
                          ) : null}
                        </div>
                      </div>
                      {group.protectionWarnings.length ? (
                        <div className="mt-3 rounded border border-yellow-400/20 bg-yellow-400/10 px-3 py-2 text-xs text-yellow-100">
                          Broker protection is not fully confirmed. Use TopstepX to verify or place protective orders manually.
                        </div>
                      ) : null}

                      {/* Manual Protection Actions - Move out of grid for visibility */}
                      {(group.executionMode === "live" ||
                        group.executionSources.includes("reconciliation")) &&
                       group.protectionStatus !== "broker_stop_confirmed" && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onSyncStop?.(group.positions[0]?.trading_account_id, group.symbol)}
                            className="h-8 px-3 text-[10px] uppercase tracking-wider font-bold border-white/20 bg-white/5 hover:bg-white/10 text-white"
                          >
                            <RefreshCw className="mr-1.5 h-3 w-3" />
                            Sync Broker Stops
                          </Button>
                          {group.intendedStopPrice && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => onReProtect?.(group.positions[0]?.trading_account_id, group.symbol)}
                              className="h-8 px-3 text-[10px] uppercase tracking-wider font-bold border-yellow-500/40 bg-yellow-500/10 text-yellow-200 hover:bg-yellow-500/20"
                            >
                              <ShieldAlert className="mr-1.5 h-3 w-3" />
                              Re-protect Position
                            </Button>
                          )}
                        </div>
                      )}

                      {group.brokerSyncWarnings.length ? (
                        <div className="mt-3 rounded border border-orange-400/20 bg-orange-400/10 px-3 py-2 text-xs text-orange-100">
                          Broker sync is unavailable right now. This is the last confirmed broker state.
                        </div>
                      ) : null}
                      <div className="mt-3 rounded border border-white/10 bg-black/20 px-3 py-2">
                        <div className="text-[10px] uppercase tracking-[0.14em] text-white/65">Entries</div>
                        <div className="mt-2 space-y-1">
                          {group.entries.map((entry) => (
                            <div key={entry.id} className="text-xs text-white/70">
                              {formatContractsLabel(entry.quantity)}, time: {entry.openedAtLabel}
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="mt-2 grid grid-cols-2 gap-2 rounded border border-white/10 bg-black/20 px-3 py-2 text-xs text-white/65">
                        <div>
                          <div className="text-[10px] uppercase tracking-[0.12em] text-white/60">Broker Position</div>
                          <div className="mt-1 break-all text-white/75">{group.brokerPositionIds.join(", ") || "—"}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-[0.12em] text-white/60">Entry Order</div>
                          <div className="mt-1 break-all text-white/75">{group.entryOrderIds.join(", ") || "—"}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-[0.12em] text-white/60">Stop Order</div>
                          <div className="mt-1 break-all text-white/75">{group.stopOrderIds.join(", ") || "—"}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-[0.12em] text-white/60">Broker Stop</div>
                          {group.brokerStopDisplay !== "—" ? (
                            <div className="mt-1 text-white/75">{group.brokerStopDisplay}</div>
                          ) : (
                            <div className="mt-1 text-red-400">Missing</div>
                          )}
                        </div>
                        {group.intendedStopPrice && (
                          <div>
                            <div className="text-[10px] uppercase tracking-[0.12em] text-white/60">Intended Stop</div>
                            <div className="mt-1 text-white/75">{formatPriceValue(group.intendedStopPrice)}</div>
                          </div>
                        )}
                        {!group.brokerConfirmedStopPrice && (
                          <div className="col-span-2 mt-2 rounded border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-200">
                            No active broker-side stop confirmed. Verify TopstepX or place protection manually.
                          </div>
                        )}
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-[0.12em] text-white/65">
                        <span>{group.executionMode === "live" ? "Live" : "Paper"}</span>
                        <span>{group.status}</span>
                        {group.brokerStatuses.map((status) => <span key={status}>{status}</span>)}
                        {group.stopProtectionStatuses.map((status) => <span key={`stop-${status}`}>Stop {status}</span>)}
                        {group.targetProtectionStatuses.map((status) => <span key={`target-${status}`}>Target {status}</span>)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="rounded border border-white/10 bg-black/20 px-3 py-3">
            <div className="text-[10px] uppercase tracking-[0.14em] text-white/65">Closed Positions</div>
            {recentClosedPositions.length === 0 ? (
              <div className="mt-2 text-xs text-white/70">No recently closed positions</div>
            ) : (
              <div className="mt-2 space-y-2">
                {!showAllTrades && allClosedSorted.length > 6 ? (
                  <div className="mb-2 text-xs text-white/65">
                    Showing 6 of {allClosedSorted.length} —{" "}
                    <button onClick={() => setShowAllTrades(true)} className="text-blue-400 hover:text-blue-300 underline underline-offset-2">
                      show all
                    </button>
                  </div>
                ) : showAllTrades && allClosedSorted.length > 6 ? (
                  <div className="mb-2 text-xs text-white/65">
                    Showing all {allClosedSorted.length} —{" "}
                    <button onClick={() => setShowAllTrades(false)} className="text-blue-400 hover:text-blue-300 underline underline-offset-2">
                      show less
                    </button>
                  </div>
                ) : null}
                {recentClosedPositions.map((trade) => (
                  <div key={trade.id} className="rounded bg-white/[0.03] px-3 py-2">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-sm font-medium text-white">
                          {trade.symbol}
                        </div>
                        <div className="mt-0.5 text-xs text-white/60">
                          {trade.side} {trade.quantity} ({String(trade.contractsLabel).toLowerCase()})
                        </div>
                        {trade.strategyName ? (
                          <div className="mt-1 text-[11px] text-blue-300">{trade.strategyName}</div>
                        ) : null}
                        {trade.historyLegTotal ? (
                          <div className="mt-1 text-[11px] text-white/72">
                            Leg {trade.historyLegIndex} of {trade.historyLegTotal}
                            {trade.brokerOrderId ? ` • Order ${trade.brokerOrderId}` : ""}
                          </div>
                        ) : trade.brokerOrderId ? (
                          <div className="mt-1 text-[11px] text-white/72">Order {trade.brokerOrderId}</div>
                        ) : null}
                        {trade.entryReason ? (
                          <div className="mt-1 text-[11px] text-white/62">
                            <span className="mr-1 uppercase tracking-[0.14em] text-white/60">Entry reason</span>
                            <span>{trade.entryReason}</span>
                          </div>
                        ) : null}
                        {trade.closeReasonInfo?.label && trade.closeReasonInfo.label !== "—" ? (
                          <div className="mt-1 text-[11px] text-white/72">
                            <span className="mr-1 uppercase tracking-[0.14em] text-white/60">Exit reason</span>
                            <span>{trade.closeReasonInfo.label}</span>
                          </div>
                        ) : null}
                        {trade.closeReasonInfo?.lines?.length ? (
                          <div className="mt-1 space-y-0.5 text-[11px] text-white/55">
                            {trade.closeReasonInfo.lines.slice(0, expandedTradeIds.has(trade.id) ? 3 : 1).map((line) => (
                              <div key={line}>{line}</div>
                            ))}
                          </div>
                        ) : null}
                      </div>
                      <div className="flex items-start gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-expanded={expandedTradeIds.has(trade.id)}
                          onClick={() => toggleExpandedTrade(trade.id)}
                          className="h-7 px-2 text-[10px] uppercase tracking-[0.14em] text-white/70 hover:bg-white/[0.06]"
                        >
                          {expandedTradeIds.has(trade.id) ? <ChevronUp className="mr-1 h-3 w-3" /> : <ChevronDown className="mr-1 h-3 w-3" />}
                          Details
                        </Button>
                        {trade.canAudit ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleOpenTradeAudit(trade.auditTradeId)}
                            className="h-7 border-white/10 bg-white/[0.03] px-2 text-[10px] uppercase tracking-[0.14em] text-white/70 hover:bg-white/[0.06]"
                          >
                            <History className="mr-1 h-3 w-3" />
                            Audit
                          </Button>
                        ) : (
                          <div className="rounded border border-white/10 bg-white/[0.03] px-2 py-1 text-[10px] uppercase tracking-[0.14em] text-white/70">
                            Broker
                          </div>
                        )}
                        <div className={`text-sm font-medium ${trade.pnl === null ? "text-white/60" : trade.pnl >= 0 ? "text-green-400" : "text-red-400"}`}>
                          {trade.pnl === null ? "Pending audit" : formatSignedCurrencyValue(trade.pnl)}
                        </div>
                      </div>
                    </div>

                    {expandedTradeIds.has(trade.id) ? (
                      <>
                    {trade.brokerPayload?.ai_post_mortem && (
                      <div className="mt-2 flex items-center gap-2">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-purple-500/10 border border-purple-500/20 cursor-help">
                                <Brain className="w-3 h-3 text-purple-400" />
                                <span className="text-[10px] text-purple-300 font-bold">
                                  {trade.brokerPayload.ai_post_mortem.rating}/10
                                </span>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-[280px] bg-zinc-900 border-purple-500/30 p-3">
                              <div className="space-y-2">
                                <div>
                                  <div className="text-[10px] uppercase text-purple-400 font-bold mb-1">AI Performance Review</div>
                                  <p className="text-xs text-zinc-300 leading-relaxed">
                                    {trade.brokerPayload.ai_post_mortem.performance_review}
                                  </p>
                                </div>
                                <div>
                                  <div className="text-[10px] uppercase text-purple-400 font-bold mb-1">Suggested Improvement</div>
                                  <p className="text-xs text-zinc-300 leading-relaxed italic">
                                    {trade.brokerPayload.ai_post_mortem.suggested_improvement}
                                  </p>
                                </div>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    )}

                    <div className="mt-3 grid grid-cols-2 gap-4 text-xs text-white/70">
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.14em] text-white/65">Entry</div>
                        <div className="mt-1 text-sm text-white">{formatPriceValue(trade.entryPrice)}</div>
                        <div className="mt-1 text-xs text-white/60">{trade.entryAtLabel}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.14em] text-white/65">Exit</div>
                        <div className="mt-1 text-sm text-white">{formatPriceValue(trade.exitPrice)}</div>
                        <div className="mt-1 text-xs text-white/60">{trade.closedAtLabel}</div>
                      </div>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-4 text-xs text-white/70">
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.14em] text-white/65">Point Value</div>
                        <div className="mt-1 text-sm text-white">
                          {trade.pointValue !== null ? `${formatCurrencyValue(trade.pointValue)} / pt` : "—"}
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.14em] text-white/65">Notional</div>
                        <div className="mt-1 text-sm text-white">{formatCurrencyValue(trade.notional)}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.14em] text-white/65">Stop</div>
                        <div className="mt-1 text-sm text-white">{trade.stopDisplay}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.14em] text-white/65">Target</div>
                        <div className="mt-1 text-sm text-white">{trade.targetDisplay}</div>
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-[0.12em] text-white/65">
                      {trade.executionMode ? <span>{trade.executionMode}</span> : null}
                      {trade.executionSourceLabel ? <span>{trade.executionSourceLabel}</span> : null}
                      {trade.closeReasonInfo?.label && trade.closeReasonInfo.label !== "—" ? <span>Closed: {trade.closeReasonInfo.label}</span> : null}
                    </div>
                      </>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </CardContent>

      <Dialog
        open={Boolean(auditTradeId)}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            setAuditTradeId(null);
            setAuditPayload(null);
            setAuditLoading(false);
            setExpandedAuditEvents(new Set());
          }
        }}
      >
        <DialogContent className="max-w-3xl border-white/10 bg-[#080808] text-white sm:max-h-[85vh] sm:overflow-y-auto">
          <DialogHeader>
            <DialogTitle>What happened in this trade</DialogTitle>
            <DialogDescription className="text-white/72">
              Follow the entry, protection, close, and broker-confirmation lifecycle. Technical payloads stay available when needed.
            </DialogDescription>
          </DialogHeader>

          {auditLoading ? (
            <div className="rounded border border-white/10 bg-white/[0.03] px-4 py-6 text-sm text-white/70">
              Loading trade audit...
            </div>
          ) : auditPayload?.error ? (
            <div className="rounded border border-red-500/20 bg-red-500/10 px-4 py-4 text-sm text-red-200">
              {auditPayload.error}
            </div>
          ) : auditPayload ? (
            <div className="space-y-4">
              <div className="grid gap-3 md:grid-cols-2">
                <div className="rounded border border-white/10 bg-white/[0.03] p-3">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-white/65">Trade</div>
                  <div className="mt-2 text-lg font-semibold text-white">
                    {auditPayload.trade?.symbol || "—"}
                  </div>
                  <div className="text-sm text-white/70">
                    {auditPayload.trade?.side || "—"} {auditPayload.trade?.quantity || 0}
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.14em] text-white/65">Entry</div>
                      <div className="mt-1 text-white">{formatPriceValue(auditPayload.trade?.entry_price)}</div>
                      <div className="mt-1 text-xs text-white/60">
                        {formatAuditTimestampValue(auditPayload.trade?.entry_time, displayTimeZone)}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.14em] text-white/65">Exit</div>
                      <div className="mt-1 text-white">{formatPriceValue(auditPayload.trade?.exit_price)}</div>
                      <div className="mt-1 text-xs text-white/60">
                        {formatAuditTimestampValue(auditPayload.trade?.exit_time, displayTimeZone)}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.14em] text-white/65">Status</div>
                      <div className="mt-1 text-white">{auditPayload.trade?.status || "—"}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.14em] text-white/65">Realized P&amp;L</div>
                      <div className={`mt-1 ${Number(auditPayload.trade?.pnl) >= 0 ? "text-green-400" : "text-red-400"}`}>
                        {formatSignedCurrencyValue(auditPayload.trade?.pnl)}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="rounded border border-white/10 bg-white/[0.03] p-3">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-white/65">Audit Summary</div>
                  <div className="mt-3 space-y-3 text-sm">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.14em] text-white/65">Entry Reason</div>
                      <div className="mt-1 text-white">
                        {auditEntryInfo.label || "—"}
                      </div>
                      {auditEntryInfo.lines.length ? (
                        <div className="mt-1 space-y-0.5 text-xs text-white/60">
                          {auditEntryInfo.lines.map((line) => (
                            <div key={line}>{line}</div>
                          ))}
                        </div>
                      ) : null}
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.14em] text-white/65">Broker Truth</div>
                      <div className="mt-1 text-white">
                        {auditPayload.audit_summary?.has_broker_truth ? "Confirmed" : "Unavailable"}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.14em] text-white/65">Exit Reason</div>
                      <div className="mt-1 text-white">
                        {auditExitInfo.label || "—"}
                      </div>
                      {auditExitInfo.lines.length ? (
                        <div className="mt-1 space-y-0.5 text-xs text-white/60">
                          {auditExitInfo.lines.map((line) => (
                            <div key={line}>{line}</div>
                          ))}
                        </div>
                      ) : null}
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.14em] text-white/65">Broker Close Reason</div>
                      <div className="mt-1 text-white/75">
                        {auditPayload.audit_summary?.close_reason || "—"}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.14em] text-white/65">Parity Signature</div>
                      <div className="mt-1 break-all font-mono text-xs text-white/75">
                        {auditPayload.audit_summary?.parity_signature || "—"}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.14em] text-white/65">Strategy Snapshot</div>
                      <div className="mt-1 text-white">
                        {auditPayload.strategy_snapshot?.strategy_name || auditPayload.audit_summary?.strategy_name || "Manual"}
                      </div>
                      <div className="mt-1 text-xs text-white/60">
                        Source: {auditPayload.strategy_snapshot?.source || "—"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded border border-white/10 bg-white/[0.03] p-3">
                <div className="text-[10px] uppercase tracking-[0.16em] text-white/65">Execution Timeline</div>
                {timelineEvents.length === 0 ? (
                  <div className="mt-3 text-sm text-white/72">No timeline events were captured for this trade.</div>
                ) : (
                  <div className="mt-3 space-y-3">
                    {timelineEvents.map((event, index) => (
                      <div key={`${event?.at || "event"}:${index}`} className="rounded border border-white/10 bg-black/20 p-3">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div>
                            <div className="text-sm font-medium text-white">
                              {formatTimelineTypeLabel(event?.type)}
                            </div>
                            <div className="mt-1 text-xs text-white/72">
                              {formatAuditTimestampValue(event?.at, displayTimeZone)}
                              {event?._repeatCount > 1 && event?._lastAt !== event?.at ? (
                                <> to {formatAuditTimestampValue(event._lastAt, displayTimeZone)}</>
                              ) : null}
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2 text-[10px] uppercase tracking-[0.12em]">
                            {event?.status ? (
                              <span className="rounded border border-white/10 bg-white/[0.03] px-2 py-1 text-white/60">
                                {event.status}
                              </span>
                            ) : null}
                            {event?.broker_status ? (
                              <span className="rounded border border-blue-400/20 bg-blue-500/10 px-2 py-1 text-blue-300">
                                {event.broker_status}
                              </span>
                            ) : null}
                            {event?._repeatCount > 1 ? (
                              <span className="rounded border border-amber-400/20 bg-amber-500/10 px-2 py-1 text-amber-200">
                                Repeated {event._repeatCount} times
                              </span>
                            ) : null}
                          </div>
                        </div>
                        {event?.details && Object.keys(event.details).length > 0 ? (
                          <div className="mt-3 space-y-2">
                            <div className="grid gap-1 text-xs text-white/65 sm:grid-cols-2">
                              {Object.entries(event.details)
                                .filter(([, value]) => ["string", "number", "boolean"].includes(typeof value))
                                .slice(0, 4)
                                .map(([key, value]) => (
                                  <div key={key} className="truncate" title={`${humanizeReasonCode(key)}: ${String(value)}`}>
                                    <span className="text-white/45">{humanizeReasonCode(key)}:</span>{" "}
                                    <span className="text-white/80">{String(value)}</span>
                                  </div>
                                ))}
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              aria-expanded={expandedAuditEvents.has(`${event?.at || "event"}:${event?.type || "unknown"}:${index}`)}
                              onClick={() => toggleExpandedAuditEvent(`${event?.at || "event"}:${event?.type || "unknown"}:${index}`)}
                              className="h-7 px-2 text-[10px] uppercase tracking-[0.14em] text-white/60 hover:bg-white/[0.05]"
                            >
                              {expandedAuditEvents.has(`${event?.at || "event"}:${event?.type || "unknown"}:${index}`) ? (
                                <ChevronUp className="mr-1 h-3 w-3" />
                              ) : (
                                <ChevronDown className="mr-1 h-3 w-3" />
                              )}
                              Technical details
                            </Button>
                            {expandedAuditEvents.has(`${event?.at || "event"}:${event?.type || "unknown"}:${index}`) ? (
                              <pre className="overflow-x-auto rounded border border-white/10 bg-[#050505] p-3 text-[11px] leading-relaxed text-white/70">
                                {JSON.stringify(event.details, null, 2)}
                              </pre>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="rounded border border-white/10 bg-white/[0.03] p-3">
                <div className="text-[10px] uppercase tracking-[0.16em] text-white/65">Execution Jobs</div>
                {executionJobs.length === 0 ? (
                  <div className="mt-3 text-sm text-white/72">No execution jobs were recorded for this trade.</div>
                ) : (
                  <div className="mt-3 space-y-2">
                    {executionJobs.map((job) => (
                      <div key={job.id} className="rounded border border-white/10 bg-black/20 px-3 py-2">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="text-sm font-medium text-white">{job.job_type || "Execution job"}</div>
                          <div className="text-xs uppercase tracking-[0.12em] text-white/72">{job.status || "—"}</div>
                        </div>
                        <div className="mt-1 text-xs text-white/72">
                          Created {formatAuditTimestampValue(job.created_at, displayTimeZone)}
                        </div>
                        {job.error_message ? (
                          <div className="mt-2 text-xs text-red-300">{job.error_message}</div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="rounded border border-white/10 bg-white/[0.03] px-4 py-6 text-sm text-white/70">
              Select a closed trade to inspect its broker-backed replay.
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default React.memo(PositionsPanel);
